
import React from "react"
import { X } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

const bannerVariants = cva(
  "relative w-full px-4 py-3 flex items-center justify-between gap-4 text-sm",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground border-b",
        primary: "bg-node-copper text-white",
        destructive: "bg-destructive text-destructive-foreground",
        success: "bg-green-500 text-white",
        warning: "bg-yellow-500 text-white",
        info: "bg-blue-500 text-white",
        purple: "bg-purple-500 text-white",
      },
      position: {
        top: "fixed top-0 left-0 right-0 z-50",
        bottom: "fixed bottom-0 left-0 right-0 z-50",
        static: "relative",
      }
    },
    defaultVariants: {
      variant: "default",
      position: "top",
    },
  }
)

interface BannerProps extends 
  React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof bannerVariants> {
  onClose?: () => void;
  showClose?: boolean;
}

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, variant, position, onClose, showClose = true, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(bannerVariants({ variant, position }), className)}
        {...props}
      >
        <div className="flex-1 flex items-center gap-2">
          {children}
        </div>
        {showClose && onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-6 w-6 shrink-0 rounded-full p-0 text-white/80 hover:text-white hover:bg-white/20"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>
    )
  }
)
Banner.displayName = "Banner"

export { Banner, bannerVariants }
